import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { FeatureCollection, MultiPolygon } from "geojson";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import {
    CROP_ZONE_FILL_LAYER,
    CROP_ZONE_LINE_LAYER,
    CROP_ZONE_SOURCE,
} from "@/lib/map/layers";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    moveLayersToTop,
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerVisibility,
} from "@/lib/utils/map";
import { DEFAULT_CROP_ZONE_COLOR } from "@/types/cropZone";

const LAYER_IDS = [CROP_ZONE_FILL_LAYER.id, CROP_ZONE_LINE_LAYER.id];

type CropZoneHighlightApi = {
    setHoveredZoneId: (id: number | null) => void;
    setEditingZoneId: (id: number | null) => void;
    setHighlightsSuppressed: (suppressed: boolean) => void;
};

const CropZoneHighlightContext = createContext<CropZoneHighlightApi | null>(
    null,
);

const noopApi: CropZoneHighlightApi = {
    setHoveredZoneId: () => undefined,
    setEditingZoneId: () => undefined,
    setHighlightsSuppressed: () => undefined,
};

// eslint-disable-next-line react-refresh/only-export-components
export function useCropZoneHighlight() {
    return useContext(CropZoneHighlightContext) ?? noopApi;
}

function visibleCropZones(
    collection:
        | FeatureCollection<
              MultiPolygon,
              { id?: number; name?: string; color?: string }
          >
        | undefined,
    editingZoneId: number | null,
) {
    const features = (collection?.features ?? [])
        .filter((feature) => {
            if (editingZoneId == null) return true;
            const id = feature.id ?? feature.properties?.id;
            return String(id) !== String(editingZoneId);
        })
        .map((feature) => {
            const id = feature.id ?? feature.properties?.id;
            return {
                type: "Feature" as const,
                id: id ?? undefined,
                geometry: feature.geometry,
                properties: {
                    id,
                    name: feature.properties?.name,
                    color: feature.properties?.color || DEFAULT_CROP_ZONE_COLOR,
                },
            };
        });
    return {
        type: "FeatureCollection" as const,
        features,
    };
}

function hoverMatch(hoveredZoneId: number | null) {
    return ["==", ["to-number", ["get", "id"]], hoveredZoneId ?? -1];
}

function applyHoverPaint(
    map: ReturnType<typeof useMap>["map"],
    hoveredZoneId: number | null,
) {
    if (!map?.getLayer(CROP_ZONE_FILL_LAYER.id)) return;
    map.setPaintProperty(CROP_ZONE_FILL_LAYER.id, "fill-opacity", [
        "case",
        hoverMatch(hoveredZoneId),
        0.7,
        0.35,
    ]);
    if (!map.getLayer(CROP_ZONE_LINE_LAYER.id)) return;
    map.setPaintProperty(CROP_ZONE_LINE_LAYER.id, "line-width", [
        "case",
        hoverMatch(hoveredZoneId),
        3.5,
        2,
    ]);
    map.setPaintProperty(CROP_ZONE_LINE_LAYER.id, "line-opacity", [
        "case",
        hoverMatch(hoveredZoneId),
        1,
        0.9,
    ]);
}

export function CropZoneHighlightProvider({
    children,
}: {
    children: ReactNode;
}) {
    const { map, mapLoaded } = useMap();
    const { geometryQuery } = useFieldDashboard() ?? {};
    const [hoveredZoneId, setHoveredZoneId] = useState<number | null>(null);
    const [editingZoneId, setEditingZoneId] = useState<number | null>(null);
    const [highlightsSuppressed, setHighlightsSuppressed] = useState(false);

    const api = useMemo(
        () => ({
            setHoveredZoneId,
            setEditingZoneId,
            setHighlightsSuppressed,
        }),
        [],
    );

    const cropZones = geometryQuery?.data?.crop_zones;

    useEffect(() => {
        if (!map || !mapLoaded) return;
        return () => {
            removeLayersIfExists(map, LAYER_IDS);
            removeSourcesIfExists(map, [CROP_ZONE_SOURCE]);
        };
    }, [map, mapLoaded]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        const data = visibleCropZones(cropZones, editingZoneId);
        addUpdateGeojsonSource(map, CROP_ZONE_SOURCE, {
            type: "geojson",
            data,
            tolerance: 0,
        });
        addUpdateGeojsonLayers(map, [
            CROP_ZONE_FILL_LAYER,
            CROP_ZONE_LINE_LAYER,
        ]);
        const visible = !highlightsSuppressed && data.features.length > 0;
        updateLayerVisibility(map, LAYER_IDS, visible);
        if (visible && editingZoneId == null) {
            moveLayersToTop(map, LAYER_IDS);
        }
    }, [cropZones, editingZoneId, highlightsSuppressed, map, mapLoaded]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        applyHoverPaint(map, hoveredZoneId);
    }, [
        cropZones,
        editingZoneId,
        highlightsSuppressed,
        hoveredZoneId,
        map,
        mapLoaded,
    ]);

    return (
        <CropZoneHighlightContext.Provider value={api}>
            {children}
        </CropZoneHighlightContext.Provider>
    );
}
